import { LevelSim, SimConstants, Tower, TowerArchetype, hexBonusOf, isAura, isBarracks, isBeam, isHex, isMortar } from "@highmarch/domain";
import { formatRange, shotsPerSecond } from "./upgradePreview";

/**
 * What makes each TOWERS_V2 tower different, in the words the UI shows (docs/TOWERS_V2.md): the Mortar's burst and
 * blind ring, the Barracks' soldiers, the Arcane Spire's ramp, the Hex Totem's curse. One place for the tower sheet
 * (towerInfo.ts), the hub's upgrade sheet, the Almanac and the cards, so the numbers always come from the balance and
 * read the same everywhere. DOM-free (unit-tested). The eight older towers have no role line (their DAMAGE / RANGE /
 * DPS say it all) and get null.
 */

const pct = (f: number) => `${Math.round(f * 100)}%`;
const secs = (ticks: number) => `${+(ticks / SimConstants.TicksPerSecond).toFixed(1)} s`;

/** The archetype's one-line role (tier 1, level 1), or null for a tower without one. */
export function roleLine(a: TowerArchetype): string | null {
  if (isMortar(a)) return `Lobs shells that burst ${formatRange(a.splashRadius.toNumber())} round where they land · can't hit within ${formatRange(a.minRange.toNumber())}`;
  if (isBarracks(a)) return `${a.soldiers} soldiers block the road · ${a.soldierHp.toDecimalString(0)} HP · back in ${secs(a.soldierRespawnTicks)}`;
  if (isBeam(a)) return `Beam grows ×1 → ×${a.rampMax.toDecimalString(0)} over ${secs(a.rampTicks)} on one raider · ignores armour`;
  if (isHex(a)) return `Curse: raiders take +${pct(a.hexDamageTaken.toNumber())} damage · healing −${pct(a.hexHealCut.toNumber())}`;
  return null;
}

/** A short tag for the card and Almanac (what kind of tower it is), or null. */
export function roleTag(a: TowerArchetype): string | null {
  if (isMortar(a)) return "Long-range splash";
  if (isBarracks(a)) return "Blocks the road";
  if (isBeam(a)) return "Ramping beam";
  if (isHex(a)) return "Curse aura";
  return null;
}

/**
 * The hub upgrade sheet's stat line for an archetype: range, damage and rate for a shooter; the TOWERS_V2 towers say
 * what their numbers mean (a soldier's blow, a beam's pulse, a curse). A tower without damage shows what it does instead
 * (a Frost tower's slow, a Beacon's aura), never "Damage 0". Shots a second as the sim fires them (shotsPerSecond).
 */
export function archetypeStats(a: TowerArchetype, damageMult = 1): string {
  // the damage at the tower's own level (the sheet used to show level 1's number beside "damage ×1.07")
  const hit = (d: number, places: number) => (d * damageMult).toFixed(places);
  const range = `Range ${formatRange(a.range.toNumber())}`;
  if (isHex(a)) return `${range} · curse +${pct(a.hexDamageTaken.toNumber())} · healing −${pct(a.hexHealCut.toNumber())}`;
  if (isBarracks(a)) return `Post within ${a.range.toDecimalString(1)} · ${a.soldiers} soldiers · ${hit(a.damage.toNumber(), 0)} per blow`;
  if (isBeam(a)) return `${range} · ${hit(a.damage.toNumber(), 1)} × ${shotsPerSecond(a.fireIntervalTicks).toFixed(0)}/s · ramps to ×${a.rampMax.toDecimalString(0)}`;
  if (isAura(a)) return `${range} · aura +${pct(a.auraDamageBonus.toNumber())} damage${a.auraPointsBonus.isPositive() ? ` · +${pct(a.auraPointsBonus.toNumber())} points` : ""}`;
  const rate = `${shotsPerSecond(a.fireIntervalTicks).toFixed(1)} shots/s`;
  if (a.damage.isZero()) return `${range} · Slow ${pct(a.slowFraction.toNumber())} for ${secs(a.slowDurationTicks)} · ${rate}`;
  return `${range} · Damage ${hit(a.damage.toNumber(), 0)} · ${rate}${isMortar(a) ? ` · min ${a.minRange.toDecimalString(1)}` : ""}`;
}

/** What the in-battle tower sheet shows differently for a TOWERS_V2 tower (towerInfo.ts). */
export interface SheetFacts {
  /** Replaces the DAMAGE plate (label, value), e.g. the Hex Totem's CURSE. */
  readonly damage: readonly [string, string] | null;
  /** Replaces the DPS plate's value. */
  readonly dps: string | null;
  /** A line under the plates, with the live numbers (soldiers standing, the curse's strength). */
  readonly line: string | null;
  /** The target priority row makes sense (the Barracks' soldiers and the totem pick for themselves). */
  readonly priority: boolean;
}

export function sheetFacts(sim: LevelSim, t: Tower): SheetFacts {
  const a = sim.balance.towers[t.archetype];
  const dmg = sim.damageOf(t).toNumber(), perSecond = SimConstants.TicksPerSecond / (sim.fireIntervalOf(t) + 1);
  if (isMortar(a)) {
    const shell = sim.shells.find((s) => s.towerId === t.id) ?? null;
    const burst = shell ? shell.radius.toNumber() : a.splashRadius.toNumber();
    return { damage: null, dps: null, line: `Shells burst ${formatRange(burst)} round where they land · blind within ${formatRange(a.minRange.toNumber())}`, priority: true };
  }
  if (isBarracks(a)) {
    const q = sim.squads.find((s) => s.towerId === t.id);
    const members = q?.members ?? [];
    const up = members.filter((m) => m.alive).length;
    const hp = members.length > 0 ? members[0].maxHp.toNumber() : a.soldierHp.toNumber();
    const armour = members.length > 0 ? members[0].armour.toNumber() : 0;
    return {
      damage: null, dps: (dmg * perSecond * Math.max(1, members.length)).toFixed(0),
      line: `${up}/${members.length} soldiers · ${hp.toFixed(0)} HP · armour ${pct(armour)} · back in ${secs(a.soldierRespawnTicks)}${q?.post ? "" : " · no road in reach"}`,
      priority: false,
    };
  }
  if (isBeam(a)) {
    const top = 1 + (a.rampMax.toNumber() - 1);
    return { damage: null, dps: `${(dmg * perSecond).toFixed(0)}–${(dmg * perSecond * top).toFixed(0)}`, line: `Beam ×1 → ×${a.rampMax.toDecimalString(0)} over ${secs(a.rampTicks)} on one raider · ignores armour`, priority: true };
  }
  if (isHex(a)) {
    const bonus = hexBonusOf(sim, t).toNumber();
    const cursed = sim.enemySlots.filter((e) => e.alive && e.hexTowerId === t.id).length;
    return { damage: ["CURSE", `+${pct(bonus)}`], dps: "aura", line: `+${pct(bonus)} damage taken · healing −${pct(a.hexHealCut.toNumber())} · ${cursed} cursed now`, priority: false };
  }
  return { damage: null, dps: null, line: null, priority: true };
}
